import React from 'react'
import PropTypes from 'prop-types'
import { Redirect, Link } from 'react-router-dom'
import glamorous from 'glamorous'
import { graphql, withApollo } from 'react-apollo'
import { flowRight as compose } from 'lodash'
import { ProductDiv } from '../shared/glamorous-style'
import Button from '../shared/button'
import getStoreInfo from '../../graphql/getStoreInfo'
import { log, handleSessionError } from '../../utils/error-tracking'
import GoBackLink from '../shared/go-back-link'
import { trackImpression, trackAction } from '../../utils/analytics'
import paymentAdvice from '../../graphql/paymentAdvice'
import clientData from '../../utils/client-data'
import createPayment from '../../graphql/createPayment'
import { CurrencyFormat, DateTimeFormat } from '@paypalcorp/worldready'
import {
  Message,
  ContextConsumer as WorldReadyContextConsumer,
} from '@paypalcorp/worldready-react'

const Header = glamorous.div({
  paddingTop: '36px',
  paddingBottom: '24px',
  textAlign: 'center',
  fontSize: '30px',
  fontWeight: '300',
  color: '#4a4a4a',
})

const Content = glamorous.div({
  margin: '0 174px',
  '@media only screen and (max-width: 768px)': {
    margin: '0 12px',
  },
})

const Img = glamorous.img({
  width: '189px',
  height: '120px',
  backgroundColor: '#ffffff',
})

const TopSection = glamorous.section({
  textAlign: 'center',
})

const MidSection = glamorous.section({
  color: '#2C2E2F',
})

const BottomSection = glamorous.section({
  marginTop: '64px',
  color: '#9DA3A6',
  fontSize: '13px',
})

const Row = glamorous.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
})

const Left = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  width: '50%',
  textAlign: 'left',
})

const Right = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  width: '50%',
  textAlign: 'right',
})

const Hr = glamorous.hr({
  borderTop: '1px dotted #9DA3A6',
})

const ButtonContainer = glamorous.section('vx_btn-container', {
  margin: '0 auto',
  width: '270px',
  padding: '40px 0',
})

const Label = glamorous.label({
  color: '#6C7378',
  fontWeight: '500',
  fontSize: '15px',
  margin: 0,
})

const EmailText = glamorous.label({
  color: '#6C7378',
  display: 'block',
  marginBottom: '40px',
})

const ProductName = glamorous.label({
  color: '#2C2E2F',
  fontSize: '15px',
  marginTop: '15px',
  marginBottom: '40px',
})

const Price = glamorous.label({
  color: '#2C2E2F',
  fontSize: '30px',
  marginBottom: '16px',
})

const BackLink = glamorous.div({
  fontSize: '13px',
  color: '#0070BA',
  cursor: 'pointer',
  marginBottom: '48px',
  marginTop: '16px',
})

const Note = glamorous.div({
  marginBottom: '16px',
})

const BuyNowTerms = glamorous.div({})

const TermsAndConditions = glamorous.div(
  {
    color: '#9da3a6',
  },
  'vx_text-body-sm',
)

const TermsTitle = glamorous.div(
  {
    paddingBottom: '12px',
    color: '#0070ba',
    fontSize: '15px',
    cursor: 'pointer',
    ':focus': {
      outline: 'none',
    },
  },
  'vx_text-body-md',
)

export class OrderConfirmation extends React.Component {
  static propTypes = {
    history: PropTypes.shape({
      location: PropTypes.shape({
        state: PropTypes.object,
      }),
      replace: PropTypes.func,
    }).isRequired,
    storeInfo: PropTypes.shape({
      loading: PropTypes.bool,
      error: PropTypes.object,
      getStoreInfo: PropTypes.shape({
        store_id: PropTypes.string,
        terms_and_conditions: PropTypes.string,
        locale: PropTypes.string,
      }),
    }),
    createPayment: PropTypes.func.isRequired,
  }

  constructor(props) {
    super(props)
    this.state = {
      payload: props.history.location.state,
      hidePaymentAdvice: true,
      paymentAdviceData: {},
      creatingPayment: false,
      terms: [
        {
          title: (
            <Message
              id="pages/cart-summary.cartsummary.proudctTerms"
              name={props.history.location.state.productName}
            />
          ),
          content: props.history.location.state.productTermsAndConditions,
          expandTerms: false,
        },
      ],
    }
    this.props
      .paymentAdvice()
      .then((res) => {
        this.setState({
          hidePaymentAdvice: false,
          paymentAdviceData: res.data.paymentAdvice,
        })
      })
      .catch((err) => {
        log({
          file: 'order-confirmation-page',
          err: {
            stack: err,
          },
        })
      })
  }

  componentDidMount() {
    window.scrollTo(0, 0)

    trackImpression(
      'main:consumer:gifts:home:',
      'main:consumer:gifts:home:orderConfirmation:::',
    )
  }

  getCheckoutOrigin = () => {
    const origin = window.location.origin
    return origin.indexOf('localhost') !== -1
      ? 'https://www.msmaster.qa.paypal.com'
      : origin
  }

  handleCheckout = () => {
    const {
      productName,
      productTermsAndConditions,
      urlKey,
      imgUrl,
      isGifting,
      senderName,
      note,
      selectedDay,
      discounts,
      sku,
    } = this.state.payload

    let { customAmount } = this.state.payload
    let { total_value, amount_to_pay } = this.state.paymentAdviceData

    let cartInfo = {
      cartItems: [
        {
          quantity: 1,
          productName,
          productTermsAndConditions,
          urlKey,
          sku,
          imgUrl,
          isGifting,
          recipientEmail: clientData.recipientEmail,
          senderName,
          note,
          selectedDay,
          customAmount,
          amountToPay: amount_to_pay,
          totalAmount: total_value,
          discounts,
          inContextFlow: true,
        },
      ],
    }

    this.setState({
      creatingPayment: true,
    })

    this.props
      .createPayment({
        variables: {
          cartInfo,
          stctype: '',
          sessionData: {
            utm_source: sessionStorage.getItem('utm_source') || '',
          },
        },
      })
      .then(({ data }) => {
        if (!data || !data.createPayment.ecToken) {
          log({
            file: 'order-confirmation',
            err: {
              stack:
                'Could not get ecToken, cancelling cart and redirecting to error page',
            },
          })
          this.props.history.replace('/error')
        }

        // redirect to Express Checkout
        const origin = this.getCheckoutOrigin()
        window.location.replace(
          `${origin}/cgi-bin/webscr?cmd=_express-checkout&useraction=commit&token=${data.createPayment.ecToken}`,
        )
      })
      .catch(() => {
        log({
          file: 'order-confirmation',
          err: {
            stack:
              'Exception in create payment promise, redirect to error page',
          },
        })
        this.props.history.replace('/error')
      })
  }

  trackClick = () => {
    trackAction('chooseDifferent')
  }

  renderOrderDetails = (worldready) => {
    const { payload, terms, paymentAdviceData } = this.state
    const totalAmountvalue = paymentAdviceData.total_value.value
    const { amount_to_pay } = { ...paymentAdviceData }
    const { value, currency_code } = amount_to_pay

    const currencyFormatter = new CurrencyFormat(worldready, {
      currency: currency_code,
    })
    const dateFormatter = new DateTimeFormat(worldready)
    const formattedDate = dateFormatter.format(new Date(payload.selectedDay))
    const today = dateFormatter.format(new Date())

    return (
      <Content>
        <TopSection>
          <section>
            <Label>
              <Message id="pages/cart-summary.confirmation.sendTo" />:
            </Label>
            <EmailText>{clientData.recipientEmail}</EmailText>
          </section>

          <section>
            <div className="col-md-12" id="item">
              <Img src={payload.imgUrl} alt={payload.productName} />
            </div>
            <ProductName>{payload.productName}</ProductName>
          </section>

          <section>
            {totalAmountvalue === value ? (
              <Price>{currencyFormatter.format(value)}</Price>
            ) : (
              <React.Fragment>
                <Price
                  style={{ display: 'block', textDecoration: 'line-through' }}
                >
                  {currencyFormatter.format(totalAmountvalue)}
                </Price>
                <Price style={{ color: '#d20000' }}>
                  {currencyFormatter.format(value)}
                </Price>
              </React.Fragment>
            )}
            <BackLink>
              <Link
                to="/categories/send-a-gift"
                style={{ textDecoration: 'none' }}
                onClick={this.trackClick}
              >
                <Message id="pages/cart-summary.confirmation.chooseDifferent" />
              </Link>
            </BackLink>
          </section>
        </TopSection>
        <MidSection>
          <Row>
            <Left>
              <Message id="pages/cart-summary.cartsummary.date" />
            </Left>
            <Right>
              {formattedDate === today ? (
                <Message id="pages/cart-summary.cartsummary.defaultDate" />
              ) : (
                formattedDate
              )}
            </Right>
          </Row>
          <Hr />
          <Row>
            <Left>
              <Message id="pages/cart-summary.cartsummary.total" />
            </Left>
            <Right>{currencyFormatter.format(value)}</Right>
          </Row>
        </MidSection>
        <BottomSection>
          <Note>
            <Message id="pages/cart-summary.cartsummary.note" />
          </Note>
          {terms.map((item, idx) => (
            <TermsAndConditions key={idx} id="product-terms-conditions">
              <TermsTitle
                tabIndex="0"
                className="vx_text-body-md"
                onClick={() => this.toggleTerms(idx)}
                onKeyPress={() => this.toggleTermsKeyPress(idx)}
              >
                {item.title}
                {item.expandTerms ? (
                  <span className="vx_icon vx_icon-small vx_icon-arrow-up-small" />
                ) : (
                  <span className="vx_icon vx_icon-small vx_icon-arrow-down-small" />
                )}
              </TermsTitle>
              {item.expandTerms && (
                <div
                  style={{ paddingTop: '12px' }}
                  dangerouslySetInnerHTML={{
                    __html: item.content,
                  }}
                />
              )}
            </TermsAndConditions>
          ))}
          <BuyNowTerms>
            <Message id="pages/cart-summary.cartsummary.terms" />
          </BuyNowTerms>
        </BottomSection>
      </Content>
    )
  }

  setVisibilityByIdx = (idx) => {
    const terms = this.state.terms
    terms[idx].expandTerms = !this.state.terms[idx].expandTerms
    trackAction(`${terms[idx].title}:expanded:${terms[idx].expandTerms}`)
    return terms
  }

  toggleTerms = (idx) => {
    this.setState({
      terms: this.setVisibilityByIdx(idx),
    })
  }

  setTermsAndConditions = () => {
    const { terms } = this.state
    if (terms.length === 2) return
    terms.push({
      title: <Message id="pages/cart-summary.cartsummary.ppdgTermsTitle" />,
      content: this.props.storeInfo.getStoreInfo.terms_and_conditions,
    })
  }

  toggleTermsKeyPress = (event, idx) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      this.toggleTerms(idx)
    }
  }

  render() {
    const { storeInfo } = this.props
    const { hidePaymentAdvice, payload, creatingPayment } = this.state

    if (storeInfo.loading || hidePaymentAdvice || creatingPayment) {
      return (
        <div className="vx_has-spinner-large" style={{ paddingTop: '400px' }} />
      )
    }

    if (storeInfo.error) {
      handleSessionError(storeInfo.error)
      return <Redirect to="/error" />
    }

    this.setTermsAndConditions()

    return (
      <WorldReadyContextConsumer>
        {(worldready) => (
          <div>
            <GoBackLink
              cssStyle={{ width: '800px' }}
              path={payload.urlKey}
              isInternal={true}
              pagename="main:consumer:gifts:home:orderConfirmation"
            />
            <ProductDiv id="order-confirmation-page" style={{ marginTop: 0 }}>
              <div>
                <Header className="vx_text-2">
                  <Message id="pages/cart-summary.confirmation.header" />
                </Header>
                {this.renderOrderDetails(worldready)}
              </div>
              <ButtonContainer>
                <Button
                  text={<Message id="pages/cart-summary.cartsummary.btn" />}
                  type="primary"
                  handleClick={this.handleCheckout}
                  pageName="main:consumer:gifts:cart"
                />
              </ButtonContainer>
            </ProductDiv>
          </div>
        )}
      </WorldReadyContextConsumer>
    )
  }
}

const OrderConfirmationWithData = compose(
  withApollo,
  graphql(getStoreInfo, { name: 'storeInfo' }),
  graphql(paymentAdvice, {
    options: (props) => ({
      variables: {
        paymentAdviceInput: props.location.state.paymentAdviceInput,
      },
    }),
    name: 'paymentAdvice',
  }),
  graphql(createPayment, { name: 'createPayment' }),
)(OrderConfirmation)

export default OrderConfirmationWithData
